import * as React from 'react';

import { Spinner } from '@/components/Elements/Spinner';
import { tv, VariantProps } from 'tailwind-variants';

const styles = tv({
  base: 'flex items-center justify-center gap-x-2 rounded-md font-medium hover:opacity-80 focus:outline-none disabled:cursor-not-allowed disabled:opacity-70',
  variants: {
    color: {
      primary: 'bg-primary-teal text-white',
      secondary: 'bg-rw-sub text-white',
      inverse: 'bg-white text-primary-teal',
      danger: 'bg-rw-red text-white',
      dark: 'bg-primary-chic-teal text-white',
      transparent: 'border-none bg-transparent text-gray-400 !shadow-none',
    },
    size: {
      xs: 'gap-x-1 p-1 text-xs',
      sm: 'px-4 py-2 text-sm',
      md: 'px-6 py-2 text-base',
      lg: 'px-8 py-3 text-lg',
    },
    noBordered: {
      false: 'border border-gray-300 shadow-sm',
    },
  },
  defaultVariants: {
    color: 'primary',
    size: 'md',
    noBordered: false,
  },
});

export type ButtonProps = Omit<
  React.ButtonHTMLAttributes<HTMLButtonElement>,
  'color'
> &
  VariantProps<typeof styles> & {
    isLoading?: boolean;
    startIcon?: React.ReactElement;
    endIcon?: React.ReactElement;
  };

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      type = 'button',
      className,
      color,
      size,
      noBordered,
      isLoading = false,
      startIcon,
      endIcon,
      ...props
    },
    ref
  ) => {
    return (
      <button
        ref={ref}
        type={type}
        className={styles({
          color,
          size,
          noBordered,
          className,
        })}
        {...props}
      >
        {isLoading && <Spinner size="sm" className="text-current" />}
        {!isLoading && startIcon}
        <span>{props.children}</span> {!isLoading && endIcon}
      </button>
    );
  }
);

Button.displayName = 'Button';
